import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import AddEventCategory from './Models/addEventCategoryModel';
import EditEventCategoryModel from './Models/editEventCategoryModel';
import { UserDataContext } from 'context';

export default function EventCategory() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [eventCategoryData, setEventCategoryData] = useState([]);
    const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [openAddStageDialogBox, setOpenAddStageDialogBox] = useState();
    const [openEditCategoryDialogBox, setOpenEditFieldDialogBox] = useState();
    const [searchTerm, setSearchTerm] = useState('');
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filteredCategory, setFilteredCategory] = useState([]);

    const fetchEventCategories = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}event-service/event-category`, { params: { page: currentPage, limit: rowsPerPage, name: searchTerm } });
            if (response.data?.success) {
                setEventCategoryData(response.data.data);
                setFilteredCategory(response.data.data);
                setTotalItemsCount(response.data.totalItems || 0);
                setTotalPagesCount(response.data.totalPages || 0);
                setTotalPagesCount(response.data.totalPages || 0);
            } else throw new Error(response.data?.message || 'Error in fetching events category');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (id, action) => {
        if (action === 'edit') {
            handleEditCategoryDialogBox(id);
        }
        else if (action === 'delete') {
            handleDelete(id);
        }
    };

    const handleDelete = async (id) => {
        setSelectedCategoryId(id);
        setOpenDeleteDialogBox(true);
    };

    const closeDeleteDialogBox = () => {
        setOpenDeleteDialogBox(false);
    };

    const handleEditCategoryDialogBox = (categoryId) => {
        setSelectedCategoryId(categoryId);
        setOpenEditFieldDialogBox(true);
    }

    const handleEditCategoryDialogClose = () => {
        setOpenEditFieldDialogBox(false);
        fetchEventCategories();
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${apiUrl}event-service/event-category/${selectedCategoryId}`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response?.data.success) {
                toast.warning(response?.data.message || 'Category has been deleted');
            }
            else throw new Error(response?.data.message || 'Unable to delete');
        } catch (error) {
            console.error(error.response?.data.message || error.message);
            toast.error("Unable to delete category");
        } finally {
            fetchEventCategories();
            setOpenDeleteDialogBox(false);
            setIsLoading(false);
        }
    };

    const filterCategory = (term) => {
        if (!term) {
            setFilteredCategory(eventCategoryData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = eventCategoryData.filter((event) =>
            event.name.toLowerCase().includes(lowerCaseTerm) || // Filter by name
            event.description?.toLowerCase().includes(lowerCaseTerm) // Optional: Filter by description
        );
        setFilteredCategory(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterCategory(term);
    };

    useEffect(() => {
        fetchEventCategories();
    }, [bearerToken, currentPage, rowsPerPage, searchTerm]);

    const formattedDate = (date) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const columns = [
        {
            key: 'name', label: 'Categories',
            render: (row) => (
                <div className="flex items-center gap-3">
                    <img
                        src={row.icon.secure_url}
                        alt={`${row.name} icon`}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <span>{row.name}</span>
                </div>
            ),
        },
        { key: 'createdAt', label: 'Created Date', render: (row) => formattedDate(row.createdAt) },
        {
            key: 'status',
            label: 'Status',
            render: (row) => (
                <div
                    className={`border rounded ${row.isActive ? 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]' : 'bg-red-100 text-red-700 border-red-500'} w-fit h-7 flex items-center`}
                >
                    <div className="py-1 px-3">{row.isActive ? 'Active' : 'Inactive'}</div>
                </div>
            ),
        },
        // { key: 'eventCount', label: 'Event Count', render: (row) => (row.eventCount) },
    ];

    const getStatusColor = (status) => {
        const statusColors = {
            active: 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]',
            published: 'bg-[#F86624] bg-opacity-15 text-[#EF4A00] border-[#EF4A00]',
            draft: 'bg-red-100 text-red-700 border-red-500',
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700';
    };

    const handleAddStageDialogBox = () => {
        setOpenAddStageDialogBox(true);
    }
    const handleAddStageDialogClose = () => {
        setOpenAddStageDialogBox(false);
        fetchEventCategories();
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <DataTable
                    title={'Event Categories'}
                    rows={filteredCategory} columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: false, showHeaderTwo: true, viewOption: false, editOption: true, addOption: true, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true }}
                    handleDialogBoxes={handleAddStageDialogBox}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    // eventStatus={eventStatus}
                    // onStatusChange={(status) => setEventStatus(status)}
                    // categories={eventCategories}
                    // onCategoryChange={(id) => setSelectedCategoryId(id)}
                    // category={selectedCategoryName}
                    // onDateChange={(date) => setSelectedDate(date)}
                    // filteredDate={selectedDate}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    loaderStatus={isLoading}
                    addButtonTitle={'Add New Category'}
                />
            </div>
            <DeleteDialog
                open={openDeleteDialogBox}
                onClose={closeDeleteDialogBox}
                onDelete={handleDeleteConfirm}
                name={'category'}
            />
            <AddEventCategory
                open={openAddStageDialogBox}
                onClose={handleAddStageDialogClose}
            />
            <EditEventCategoryModel
                open={openEditCategoryDialogBox}
                onClose={handleEditCategoryDialogClose}
                categoryId={selectedCategoryId}
            // fetchEventCategories={fetchEventCategories}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <FooterTailwind />
        </TailwindLayout>
    )
}